import { useState } from "react";
import { Newspaper, Receipt, BellRing, UserCheck } from "lucide-react";
import { useDeleteFilter, useSaveFilter, type Filter } from "../../lib/queries";
import s from "./Settings.module.css";

type Kind = NonNullable<Filter["criteria"]["kind"]>;

const RULES: { kind: Kind; folder: string; label: string; hint: string; icon: typeof Newspaper }[] = [
  { kind: "newsletter", folder: "Newsletters", label: "Newsletters", hint: "Mailing lists and bulk mail go to Newsletters.", icon: Newspaper },
  { kind: "receipt", folder: "Receipts", label: "Receipts", hint: "Receipts, invoices and order confirmations go to Receipts.", icon: Receipt },
  { kind: "notification", folder: "Notifications", label: "Notifications", hint: "Automated mail from no-reply and notification senders goes to Notifications.", icon: BellRing },
  {
    kind: "screener",
    folder: "Screener",
    label: "Screener",
    hint: "Mail from someone new waits in Screener until you allow them. People you have written to, your colleagues, replies, and banks, cloud and code services always come straight in.",
    icon: UserCheck,
  },
];

/** One switch per ready-made rule. Each is an ordinary server-side filter, so it also works in other mail apps. */
export function QuickRules({ filters, loading }: { filters: Filter[]; loading: boolean }) {
  const save = useSaveFilter();
  const del = useDeleteFilter();
  // The switch moves at once; the server catches up (and wins if the save fails, via the refetch).
  const [pending, setPending] = useState<Partial<Record<Kind, boolean>>>({});
  return (
    <section className={s.group} aria-labelledby="quick-rules" aria-busy={loading || undefined}>
      <h3 id="quick-rules" className={s.groupTitle}>Smart folders</h3>
      {RULES.map((r) => {
        const existing = filters.find((f) => f.criteria.kind === r.kind);
        return (
          <div key={r.kind} className={s.row}>
            <div className={s.rowText}>
              <div className={s.rowLabel} style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                <r.icon aria-hidden style={{ width: 16, height: 16 }} />
                {r.label}
              </div>
              <div className={s.rowHint}>{r.hint}</div>
            </div>
            <div className={s.rowControl}>
              <input
                type="checkbox"
                role="switch"
                className={s.switch}
                aria-label={r.label}
                checked={pending[r.kind] ?? Boolean(existing?.enabled)}
                disabled={loading || save.isPending || del.isPending}
                onChange={(e) => {
                  const on = e.target.checked;
                  setPending((p) => ({ ...p, [r.kind]: on }));
                  const done = { onSettled: () => setPending((p) => ({ ...p, [r.kind]: undefined })) };
                  if (on) save.mutate(existing ? { ...existing, enabled: true } : { enabled: true, criteria: { kind: r.kind }, actions: { moveTo: r.folder } }, done);
                  else if (existing) del.mutate(existing.id, done);
                }}
              />
            </div>
          </div>
        );
      })}
    </section>
  );
}
